import { Fragment, type ReactNode } from 'react';
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import type { MatrixCell, MatrixRow, TrendDto, TrendStatus } from '@afiliados/shared';
import { cn } from '@/lib/utils';
import {
  TREND_CELL_CLASS,
  TREND_LABEL,
  TREND_TEXT_CLASS,
  fmtMoney,
  fmtPct,
  fmtTrend,
} from '@/lib/format';

export type Metric = 'gross' | 'net' | 'both';

export interface MatrixColumn {
  key: string;
  label: string;
  hint?: string;
  /** Mês com só parte dos dias importados: a variação usa projeção. */
  partial?: boolean;
  /** Rótulo da coluna de referência da variação (o mês anterior). */
  prevLabel?: string;
}

interface Props {
  columns: MatrixColumn[];
  rows: MatrixRow[];
  totals: { cells: MatrixCell[]; totalGross: number; totalNet: number } | null;
  metric: Metric;
  entityLabel: string;
  /** Mostra as colunas % do líquido e % acumulado (curva ABC). */
  withShare?: boolean;
  /** Pinta as células pela variação contra a coluna anterior. */
  heat?: boolean;
  /** Mostra a coluna Tendência (variação da última coluna) nesta métrica. */
  trendMetric?: 'gross' | 'net';
  onRowClick?: (row: MatrixRow) => void;
  emptyMessage?: ReactNode;
}

function Money({ v, strong }: { v: number | null; strong?: boolean }) {
  if (v === null) return <span className="text-muted-foreground/60">—</span>;
  return (
    <span className={cn('tabular-nums', v < 0 && 'text-destructive', strong && 'font-semibold')}>
      {fmtMoney(v)}
    </span>
  );
}

function trendTitle(t: TrendDto, prevLabel?: string): string | undefined {
  if (!t.status) return undefined;
  const base = TREND_LABEL[t.status];
  const value = t.ratio != null && t.status !== 'zero' ? ` · ${fmtTrend(t)}` : '';
  return prevLabel ? `${base}${value} vs ${prevLabel}` : `${base}${value}`;
}

function TrendValue({ t }: { t: TrendDto }) {
  if (!t.status) return <span className="text-muted-foreground/60">—</span>;
  const Icon =
    t.status === 'up' || t.status === 'up_strong' || t.status === 'new'
      ? ArrowUpRight
      : t.status === 'flat'
        ? Minus
        : ArrowDownRight;
  return (
    <span
      className={cn('inline-flex items-center gap-0.5 tabular-nums', TREND_TEXT_CLASS[t.status])}
      title={TREND_LABEL[t.status]}
    >
      <Icon className="size-3.5" />
      {fmtTrend(t)}
    </span>
  );
}

const LEGEND: TrendStatus[] = ['up_strong', 'up', 'down', 'down_strong', 'zero', 'new'];

/** Legenda das cores de variação, para colocar abaixo da matriz. */
export function TrendLegend({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground',
        className,
      )}
    >
      <span>Variação contra o mês anterior:</span>
      {LEGEND.map((st) => (
        <span key={st} className="inline-flex items-center gap-1.5">
          <span className={cn('inline-block size-3 rounded-sm', TREND_CELL_CLASS[st])} />
          {TREND_LABEL[st]}
        </span>
      ))}
      <span>Mês parcial compara pela projeção dos dias importados.</span>
    </div>
  );
}

/**
 * Matriz entidade × colunas (meses ou períodos), no desenho do Planilhão: posição, nome, um
 * bloco de colunas para o bruto e outro para o líquido, totais e participação. Cabeçalho e as
 * duas primeiras colunas ficam fixos ao rolar. Com `heat`, cada célula ganha a cor da variação
 * contra a coluna anterior.
 */
export function MatrixTable({
  columns,
  rows,
  totals,
  metric,
  entityLabel,
  withShare = true,
  heat = false,
  trendMetric,
  onRowClick,
  emptyMessage,
}: Props) {
  const blocks: { key: 'gross' | 'net'; title: string }[] =
    metric === 'both'
      ? [
          { key: 'gross', title: 'Faturamento bruto' },
          { key: 'net', title: 'Faturamento líquido' },
        ]
      : [{ key: metric, title: metric === 'gross' ? 'Faturamento bruto' : 'Faturamento líquido' }];

  if (!rows.length) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground">
        {emptyMessage ?? 'Nenhum resultado.'}
      </div>
    );
  }

  const th = 'px-3 py-2 text-right text-xs font-medium whitespace-nowrap text-muted-foreground';
  const td = 'px-3 py-1.5 text-right text-sm whitespace-nowrap';

  return (
    <div className="relative max-h-[calc(100vh-18rem)] overflow-auto">
      <table className="w-full border-separate border-spacing-0 text-sm">
        <thead className="matrix-sticky-head">
          <tr>
            <th
              className={cn(th, 'matrix-sticky-col left-0 w-10 border-b text-center')}
              rowSpan={2}
            >
              #
            </th>
            <th
              className={cn(th, 'matrix-sticky-col left-10 min-w-56 border-b border-r text-left')}
              rowSpan={2}
            >
              {entityLabel}
            </th>
            {blocks.map((b) => (
              <th
                key={b.key}
                colSpan={columns.length}
                className={cn(th, 'border-b border-r bg-muted/40 text-center font-semibold')}
              >
                {b.title}
              </th>
            ))}
            {trendMetric && (
              <th
                className={cn(th, 'border-b')}
                rowSpan={2}
                title={`Variação do último mês contra o anterior (${trendMetric === 'gross' ? 'bruto' : 'líquido'})`}
              >
                Tendência
              </th>
            )}
            <th className={cn(th, 'border-b')} rowSpan={2}>
              Total bruto
            </th>
            <th className={cn(th, 'border-b')} rowSpan={2}>
              Total líquido
            </th>
            {withShare && (
              <>
                <th
                  className={cn(th, 'border-b')}
                  rowSpan={2}
                  title="Participação no líquido total"
                >
                  % líq.
                </th>
                <th
                  className={cn(th, 'border-b')}
                  rowSpan={2}
                  title="Participação acumulada (curva ABC)"
                >
                  % acum.
                </th>
              </>
            )}
          </tr>
          <tr>
            {blocks.map((b) => (
              <Fragment key={b.key}>
                {columns.map((c, i) => (
                  <th
                    key={`${b.key}-${c.key}`}
                    className={cn(th, 'border-b', i === columns.length - 1 && 'border-r')}
                    title={c.hint}
                  >
                    {c.label}
                    {c.partial && (
                      <span className="ml-1 rounded bg-amber-400/30 px-1 text-[10px] font-normal text-amber-800 dark:text-amber-200">
                        parcial
                      </span>
                    )}
                  </th>
                ))}
              </Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              className={cn('group', onRowClick && 'cursor-pointer')}
              onClick={onRowClick ? () => onRowClick(r) : undefined}
            >
              <td
                className={cn(
                  td,
                  'matrix-sticky-col left-0 border-b text-center text-xs text-muted-foreground',
                )}
              >
                {r.rank}
              </td>
              <td
                className={cn(
                  td,
                  'matrix-sticky-col left-10 max-w-72 truncate border-b border-r text-left font-medium',
                )}
                title={r.name}
              >
                {r.name}
              </td>
              {blocks.map((b) => (
                <Fragment key={b.key}>
                  {r.cells.map((cell, i) => {
                    const t = cell.trend[b.key];
                    const colored = heat && t.status ? TREND_CELL_CLASS[t.status] : '';
                    return (
                      <td
                        key={`${b.key}-${i}`}
                        className={cn(
                          td,
                          'border-b',
                          colored || 'group-hover:bg-muted/40',
                          i === r.cells.length - 1 && 'border-r',
                        )}
                        title={heat ? trendTitle(t, columns[i]?.prevLabel) : undefined}
                      >
                        <Money v={cell[b.key]} />
                      </td>
                    );
                  })}
                </Fragment>
              ))}
              {trendMetric && (
                <td className={cn(td, 'border-b group-hover:bg-muted/40')}>
                  <TrendValue t={r.lastTrend[trendMetric]} />
                </td>
              )}
              <td className={cn(td, 'border-b group-hover:bg-muted/40')}>
                <Money v={r.totalGross} strong />
              </td>
              <td className={cn(td, 'border-b group-hover:bg-muted/40')}>
                <Money v={r.totalNet} strong />
              </td>
              {withShare && (
                <>
                  <td className={cn(td, 'border-b text-muted-foreground group-hover:bg-muted/40')}>
                    {fmtPct(r.share, 2)}
                  </td>
                  <td className={cn(td, 'border-b text-muted-foreground group-hover:bg-muted/40')}>
                    {fmtPct(r.cumulativeShare, 1)}
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
        {totals && (
          <tfoot className="sticky bottom-0 z-20 bg-card">
            <tr className="font-semibold">
              <td className={cn(td, 'matrix-sticky-col left-0 border-t')} />
              <td className={cn(td, 'matrix-sticky-col left-10 border-t border-r text-left')}>
                Total
              </td>
              {blocks.map((b) => (
                <Fragment key={b.key}>
                  {totals.cells.map((cell, i) => (
                    <td
                      key={`${b.key}-${i}`}
                      className={cn(td, 'border-t', i === totals.cells.length - 1 && 'border-r')}
                    >
                      <Money v={cell[b.key]} />
                    </td>
                  ))}
                </Fragment>
              ))}
              {trendMetric && <td className={cn(td, 'border-t')} />}
              <td className={cn(td, 'border-t')}>
                <Money v={totals.totalGross} />
              </td>
              <td className={cn(td, 'border-t')}>
                <Money v={totals.totalNet} />
              </td>
              {withShare && (
                <>
                  <td className={cn(td, 'border-t')}>100%</td>
                  <td className={cn(td, 'border-t')} />
                </>
              )}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
